import React, { useEffect } from "react";
import { styled } from '@mui/system';
import { useNavigate } from "react-router-dom";
import { TextField, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import imageUrl from "../images/image";
import InputLabel from '@mui/material/InputLabel';
import { auth, db } from "../../firebase";
import EmailIcon from '@mui/icons-material/Email';
import Input from '@mui/material/Input';
import AccountCircle from '@mui/icons-material/AccountCircle';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import IconButton from '@mui/material/IconButton';
import FormControl from '@mui/material/FormControl';
import FullPageLoader from "../FullPageLoader/FullPageLoader";
import { ref, get, set } from 'firebase/database';
import { setUserDetails } from "./userManagement";
import { signInWithEmailAndPassword, onAuthStateChanged } from "firebase/auth";
import CollegeSelector from "./CollegeSelector";

const isMobile = window.innerWidth <= 600;

const MainContainer = styled('div')({
    height: '100vh',
    display: 'flex',
    '@media (max-width: 600px)': {
        flexDirection: 'column',
    },
});

const ImageHalf = styled('div')({
    flex: 1,
    background: `url(${imageUrl})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    width: '100%',
    '@media (max-width: 600px)': {
        height: '50vh',
        backgroundSize: 'contain',
    },
});

const ContentHalf = styled('div')({
    flex: 1,
    backgroundColor: 'white',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    color: 'black',
    justifyContent: 'center',
    textAlign: 'start',
    padding: '20px',
});

const GenerateButton = styled('button')({
    marginTop: '15px',
    backgroundColor: '#2560CD',
    padding: '10px 26px',
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 500,
    color: 'white',
    cursor: 'pointer',
    height: '50px',
    borderRadius: '10px',
    fontSize: '16px',
    "&:hover": {
        background: 'black',
        color: 'white',
    },
    '@media (max-width: 768px)': {
        width: '100%',
        fontSize: '14px',
    },
});

const GeneratePaleButton = styled('button')({
    marginTop: '15px',
    backgroundColor: '#2560CD',
    opacity: '0.1',
    padding: '10px 26px',
    fontFamily: 'Poppins, sans-serif',
    color: 'white',
    cursor: 'pointer',
    height: '50px',
    fontWeight: 500,
    borderRadius: '10px',
    fontSize: '16px',
});

let emailavatar;

const HomePage = () => {
    const navigate = useNavigate();
    const [email, setEmail] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [errorSama, setError] = React.useState('');
    const [showPassword, setShowPassword] = React.useState(false);
    const [agree, setAgree] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isConsentDialogOpen, setIsConsentDialogOpen] = React.useState(false);
    const [selectedCollege, setSelectedCollege] = React.useState("");

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                checkUserAvailable(user);
            }
        });
        return () => unsubscribe();
    }, []);

    const onSubmit = async (e) => {
        e.preventDefault();

        if (!selectedCollege) {
            setError("Please select a college.");
            setTimeout(() => setError(''), 3000);
            return;
        }

        setIsLoading(true);
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;
            setUserDetails(user);
            await checkUserAvailable(user);
        } catch (error) {
            console.error("Login error:", error);
            setError("Invalid email or password.");
            setTimeout(() => setError(''), 3000);
        } finally {
            setIsLoading(false);
        }
    };


    const checkUserAvailable = async (authUser) => {
        if (authUser) {
            const userUid = authUser.uid;
            const consentRef = ref(db, `isConsent/${userUid}/`);

            try {
                const snapshot = await get(consentRef);
                if (snapshot.exists() && snapshot.val()) {
                    navigate("/dashboard");
                } else {
                    setIsConsentDialogOpen(true);
                }
            } catch (error) {
                console.error("Error checking consent status:", error);
            }
        }
    };

    const handleConsentAgree = async () => {
        const userUid = auth.currentUser.uid;
        const consentRef = ref(db, `isConsent/${userUid}/`);
        try {
            await set(consentRef, true);
        } catch (error) {
            console.error('Error setting consent status:', error);
        } finally {
            setIsConsentDialogOpen(false);
            setIsLoading(false);
            navigate("/dashboard");
        }
    };

    const handleCheckBox = () => {
        setAgree(!agree);
    };

    if (email != null) {
        emailavatar = email;
    }

    return (
        <MainContainer>
            {isLoading && <FullPageLoader />}
            <ImageHalf></ImageHalf>
            <ContentHalf>
                <Typography
                    style={{
                        fontFamily: 'Poppins, sans-serif',
                        fontSize: isMobile ? '25px' : '40px',
                        padding: '20px',
                        fontWeight: 'bold'
                    }}>
                    Vocation<span style={{ color: 'red' }}>IQ</span>
                </Typography>
                <Typography style={{
                    textDecoration: 'underline',
                    fontSize: isMobile ? '20px' : '35px',
                    textDecorationColor: 'GrayText',
                    fontFamily: 'Poppins, sans-serif'
                }}>
                    LogIn
                </Typography>
                {errorSama && <Typography variant="danger">{errorSama}</Typography>}
                <FormControl sx={{ m: 1 }} variant="standard">
                    <InputLabel htmlFor="input-with-icon-adornment">
                        Email
                    </InputLabel>
                    <Input
                        id="input-with-icon-adornment"
                        color="secondary"
                        size='medium'
                        label="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        style={{ width: isMobile ? 250 : 300 }}
                        startAdornment={
                            <EmailIcon position="start" style={{ padding: '5px' }}>
                                <AccountCircle />
                            </EmailIcon>
                        }
                    />
                </FormControl>
                <FormControl sx={{ m: 1 }} variant="standard">
                    <TextField
                        type={showPassword ? 'text' : 'password'}
                        variant='standard'
                        label="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        style={{ width: isMobile ? 250 : 300, height: '50px' }}
                        size='medium'
                        required
                        InputProps={{
                            endAdornment: (
                                <IconButton
                                    onClick={() => setShowPassword(!showPassword)}
                                    edge="end"
                                >
                                    {showPassword ? <Visibility /> : <VisibilityOff />}
                                </IconButton>
                            ),
                        }}
                    />
                </FormControl>
                <div style={{ width: isMobile ? 250 : 300, margin: '16px 0' }}>
                    <CollegeSelector
                        value={selectedCollege}
                        onChange={(newCollege) => {
                            setSelectedCollege(newCollege);
                            setUserDetails({ college: newCollege });
                        }}
                    />
                </div>
                <div style={{ display: 'flex', flexDirection: 'row', paddingTop: '10px' }}>
                    <TextField type='checkbox' checked={agree} onChange={handleCheckBox} />
                    <Typography style={{ paddingLeft: '9px', fontFamily: 'Poppins, sans-serif', fontSize: isMobile ? 14 : '16px' }}>
                        I accept the <span onClick={() => navigate('/privacypolicy')}
                            style={{ cursor: 'pointer', textDecoration: 'underline', color: 'inherit' }}>Privacy Policy</span> and the<br />
                        <span onClick={() => navigate('/termsofservice')}
                            style={{ cursor: 'pointer', textDecoration: 'underline', color: 'inherit' }}>Terms of Service</span>
                    </Typography>
                </div>
                {
                    (!agree || !selectedCollege) ? (
                        <GeneratePaleButton disabled>Log In</GeneratePaleButton>
                    ) : (
                        <GenerateButton onClick={onSubmit}>Log In</GenerateButton>
                    )
                }
            </ContentHalf>

            <Dialog
                open={isConsentDialogOpen}
                onClose={() => {
                    setIsConsentDialogOpen(false);
                    setIsLoading(false);
                }}
                aria-labelledby="consent-dialog-title"
            >
                <DialogTitle id="consent-dialog-title">Consent Form</DialogTitle>
                <DialogContent dividers style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <Typography variant="body1" style={{ fontFamily: 'Poppins, sans-serif', marginBottom: '20px' }}>
                        Informed Consent Form
                    </Typography>
                    <Typography variant="body2" style={{ fontFamily: 'Poppins, sans-serif', marginBottom: '10px' }}>
                        <strong>Title of the Study:</strong> Analyzing the impact of participation of extra-curricular activities in the academic and professional environment in discovery and determination of student's professional strengths in management post graduate students
                    </Typography>
                    <Typography variant="body2" style={{ fontFamily: 'Poppins, sans-serif', marginBottom: '10px' }}>
                        <strong>Investigator(s) Name:</strong> Sindhu Kakade
                    </Typography>
                    <Typography variant="body2" style={{ fontFamily: 'Poppins, sans-serif', marginBottom: '10px' }}>
                        Hello! I am Sindhu Kakade, a member at the Indian Institute of Management Bangalore (IIMB). This is product research and I invite you to take part in this online questionnaire.
                    </Typography>
                    <Typography variant="body2" style={{ fontFamily: 'Poppins, sans-serif', marginBottom: '10px' }}>
                        This consent form is provided to you so that you understand the purpose of the study, what you will be asked to do, potential information that you would share if you decide to be in this study, as well as your rights (confidentiality and privacy) as a research participant.
                    </Typography>
                    {/* Include all the sections from your consent form here in similar Typography components */}
                    <Typography variant="body2" style={{ fontFamily: 'Poppins, sans-serif', marginBottom: '10px' }}>
                        <strong>Why am I being asked to participate in this study?</strong>
                    </Typography>
                    <Typography variant="body2" style={{ fontFamily: 'Poppins, sans-serif', marginBottom: '10px' }}>
                        As this study is relevant to a management student, you are being invited to participate in a research study on Analyzing the impact of participation of extra-curricular and professional activities in discovery and determination of student's professional strengths in management post graduate students. The study is being conducted by Sindhu Kakade, NSRCEL, IIMB, +91-9844459162, sindhukakade@gmail.com. If you have questions, you may contact the researcher via e-mail at sindhukakade@gmail.com or over phone +91-9844459162.
                    </Typography>
                    {/* Continue adding the rest of the sections */}
                    <Typography variant="body2" style={{ fontFamily: 'Poppins, sans-serif', marginBottom: '10px' }}>
                        <strong>What is the purpose of this research?</strong>
                    </Typography>
                    <Typography variant="body2" style={{ fontFamily: 'Poppins, sans-serif', marginBottom: '10px' }}>
                        The purpose of this research is to take an account of your extracurricular activities that you have participated in your schools, colleges and any other organization to identify the innate strengths that you can utilize to carve out your learning and development strategy as well as utilize this knowledge to take wise career decisions leading to happy, satisfying and fruitful career.
                    </Typography>
                    {/* Add more sections as needed */}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setIsConsentDialogOpen(false);
                        setIsLoading(false); // Ensure loading stops when dialog is closed
                    }} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleConsentAgree} color="primary" autoFocus>
                        Agree
                    </Button>
                </DialogActions>
            </Dialog>
        </MainContainer>
    );
};

export { emailavatar };
export default HomePage;
